import * as RadixToast from '@radix-ui/react-toast';
import { type ComponentProps, type ComponentType, type ReactNode, type SVGProps, useEffect, useState } from 'react';

import { CloseIcon, ErrorIcon } from 'components/ui/icons';
import { ElementTestId } from 'enums/testing';
import { t } from 'utils/intlUtils';

import { HEIGHT_500, WIDTH_500 } from 'styles/tokens';

import {
  ActionButton,
  Body,
  CloseButton,
  IconWrapper,
  PLACEMENT_AXES,
  StyledDescription,
  StyledTitle,
  ToastContent,
  ToastRoot,
  VARIANT_CONFIG,
} from './Toast.styles';

export type ToastAction = {
  /**
   * Screen-reader description of the action. Defaults to `label`.
   */
  altText?: string;
  label: string;
  /**
   * Runs the action. Radix dismisses the toast on click, so follow-up feedback needs a fresh toast.
   */
  onClick: () => void;
};

export type ToastVariant = 'error' | 'neutral' | 'success';

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

const DEFAULT_STATUS_ICON: IconComponent = ErrorIcon;

/**
 * Every placement a stack can take, in the order their regions render.
 *
 * No mid-edge 'left' or 'right': every measurement accumulates away from a pinned edge, so a
 * vertically centred stack has no reference to accumulate from.
 */
export const TOAST_PLACEMENTS = [
  'bottom-left',
  'bottom-center',
  'bottom-right',
  'top-left',
  'top-center',
  'top-right',
] as const;

/**
 * Where a stack sits — the edge it is pinned to, and where along that edge. One stack per placement,
 * since Radix scopes a single viewport and swipe direction to each of its providers.
 */
export type ToastPlacement = (typeof TOAST_PLACEMENTS)[number];

/**
 * Swipe gesture that dismisses a toast at a given placement — always a flick back towards its edge.
 * Radix accepts one direction per `Toast.Provider`, and only the edge bears on it.
 * @param placement - where the stack sits
 */
export const getSwipeDirection = (placement: ToastPlacement): 'down' | 'up' =>
  PLACEMENT_AXES[placement].edge === 'top' ? 'up' : 'down';

interface Props extends Omit<ComponentProps<typeof RadixToast.Root>, 'children' | 'title'> {
  /**
   * Optional action button rendered flush right of the message (e.g. "Undo"). Clicking it dismisses.
   */
  action?: ToastAction;
  children: ReactNode;
  /**
   * Ref for the element the stack measures, so a collapsed card clamps to the front card's height.
   */
  contentRef?: (element: HTMLElement | null) => void;
  dismissible?: boolean;
  icon?: IconComponent;
  isExpanded?: boolean;
  isFront?: boolean;
  /**
   * Whether the card is shown at all. Defaults to true; false also takes it out of the keyboard cycle.
   */
  isVisible?: boolean;
  /**
   * Where the toast's stack sits. Must match `ToastViewport`'s placement. Defaults to 'top-center'.
   */
  placement?: ToastPlacement;
  testId?: ElementTestId;
  title?: string;
  /**
   * Visual variant, driving the card's surface, colors and how it is announced. Defaults to 'error'.
   */
  variant?: ToastVariant;
}

/**
 * One card in the notification stack.
 *
 * Always force-mounted: the entrance and exit are CSS transitions and Radix's `Presence` only
 * recognises keyframe animations, so teardown belongs to whoever renders this — `NotificationProvider`.
 * @param props - message content, variant, optional action, and this card's place in the stack
 */
const Toast = ({
  action,
  children,
  contentRef,
  dismissible = true,
  icon,
  isExpanded = false,
  isFront = true,
  isVisible = true,
  placement = 'top-center',
  testId = ElementTestId.TOAST_ROOT,
  title,
  variant = 'error',
  ...rootProps
}: Props) => {
  const { announceType, iconColor, textColor } = VARIANT_CONFIG[variant];
  const Icon = icon ?? DEFAULT_STATUS_ICON;
  const [isMounted, setIsMounted] = useState(false);

  /*
   * Deferred a frame rather than flipped in the effect body: the browser has to paint the off-edge
   * state once, or there is nothing for the entrance to transition from.
   */
  useEffect(() => {
    const frame = requestAnimationFrame(() => setIsMounted(true));

    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <ToastRoot
      $placement={placement}
      $variant={variant}
      data-expanded={isExpanded}
      data-front={isFront}
      data-mounted={isMounted}
      data-testid={testId}
      data-visible={isVisible}
      tabIndex={isVisible ? undefined : -1}
      type={announceType}
      {...rootProps}
      forceMount
    >
      <ToastContent ref={contentRef}>
        <IconWrapper>
          <Icon color={iconColor} height={HEIGHT_500} width={WIDTH_500} />
        </IconWrapper>
        <Body>
          {title ? <StyledTitle $variant={variant}>{title}</StyledTitle> : null}
          {children ? (
            <StyledDescription $variant={variant} data-testid={ElementTestId.TOAST_DESCRIPTION}>
              {children}
            </StyledDescription>
          ) : null}
        </Body>
        {action ? (
          <RadixToast.Action altText={action.altText ?? action.label} asChild>
            <ActionButton $variant={variant} data-testid={ElementTestId.TOAST_ACTION_BUTTON} onClick={action.onClick}>
              {action.label}
            </ActionButton>
          </RadixToast.Action>
        ) : null}
        {dismissible ? (
          <RadixToast.Close asChild>
            <CloseButton
              $variant={variant}
              aria-label={t('close')}
              data-testid={ElementTestId.TOAST_CLOSE_BUTTON}
              type="button"
            >
              <CloseIcon color={textColor} height={HEIGHT_500} width={WIDTH_500} />
            </CloseButton>
          </RadixToast.Close>
        ) : null}
      </ToastContent>
    </ToastRoot>
  );
};

export default Toast;
